import { ValueGetterParams } from 'ag-grid-community';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { GROUP_CELL_ID } from '@/shared/components/ui/data-table/components/ag-grid-table-core/ag-grid-table-core.constants';
import { useLazyGridValue } from '@/shared/hooks/use-lazy-grid-value/use-lazy-grid-value';
import { useLazyReadResourceGroupNameByIdQuery } from '@/shared/redux/api/assets/groups/groups-api';
import { isPermissionDeniedError } from '@/shared/redux/api/utils/error-handlers';

export const useGroupValueGetter = () => {
  const { getValue } = useLazyGridValue();
  const { t } = useTranslation();
  const [readResourceGroupNameById] = useLazyReadResourceGroupNameByIdQuery();

  const fetchGroupName = useCallback(
    async (groupId?: string) => {
      if (!groupId) {
        return null;
      }

      try {
        const data = await readResourceGroupNameById({ groupId }).unwrap();
        return data?.readResourceGroup?.name ?? null;
      } catch (error) {
        if (isPermissionDeniedError(error)) {
          return t('groups:restricted_group');
        }

        return t('groups:deleted_group');
      }
    },
    [readResourceGroupNameById, t],
  );

  const groupNameValueGetter = ({ data }: ValueGetterParams) => {
    const { raw: _, groupId, ...rest } = data;
    return getValue(
      `${JSON.stringify(rest)}-group`,
      () => fetchGroupName(groupId),
      GROUP_CELL_ID,
    );
  };

  return { groupNameValueGetter };
};
